import type {
  Mode,
  SchemaAdditional,
  SchemaConnection,
  SchemaData,
  SchemaStation,
  SchemaTapGrid,
  SchemaText,
  SchemaTransition,
} from '../schema/types'
import { elementVisibleInMode } from '../schema/types'

/**
 * Mirror of iOS `MapData.filtered(by:)` in
 * `metro-ios/MetroCore/Sources/Graph/MapData.swift:114`.
 *
 * Filters `SchemaData` by the active Night Tube `mode`:
 *
 *  1. Stations / Additional / Texts / TapGrids — keep elements whose `modes`
 *     include the active mode (missing `modes` = visible everywhere, V1-parity
 *     for legacy test fixtures).
 *  2. Connections / Transitions — keep when their `modes` include the active
 *     mode AND BOTH endpoints (`fromStationId`, `toStationId`) survive the
 *     station filter. Mirrors iOS `hasFromStation && hasToStation` predicate.
 *  3. Lines are NOT filtered separately — they are derived (a line with no
 *     surviving connections is implicitly hidden because nothing references it
 *     in the render path).
 *
 * The function is pure and idempotent: returns a brand-new SchemaData with
 * filtered arrays. Caller can run it on every mode change; downstream
 * consumers (routingAdapter, renderer) operate on consistent shape.
 */
export function filterMapDataByMode(data: SchemaData, mode: Mode): SchemaData {
  const filteredStations: readonly SchemaStation[] = data.stations.filter((s) =>
    elementVisibleInMode(s.modes, mode),
  )
  const stationIds = new Set(filteredStations.map((s) => s.id))

  const filteredConnections: readonly SchemaConnection[] = data.connections.filter(
    (c) =>
      elementVisibleInMode(c.modes, mode) &&
      endpointSurvives(c.fromStationId, stationIds) &&
      endpointSurvives(c.toStationId, stationIds),
  )

  const filteredTransitions: readonly SchemaTransition[] | undefined =
    data.transitions === undefined
      ? undefined
      : data.transitions.filter(
          (t) =>
            elementVisibleInMode(t.modes, mode) &&
            stationIds.has(t.fromStationId) &&
            stationIds.has(t.toStationId),
        )

  const filteredAdditional: readonly SchemaAdditional[] = data.additional.filter((a) =>
    elementVisibleInMode(a.modes, mode),
  )

  const filteredTexts: readonly SchemaText[] | undefined =
    data.texts === undefined
      ? undefined
      : data.texts.filter((t) => elementVisibleInMode(t.modes, mode))

  // Belt-and-braces: the extra `stationIds.has(...)` clause is web-only —
  // iOS / Android drop normal-only TapGrids via the modes predicate alone.
  // QA flagged the divergence 2026-06-01; kept because the prod schema can
  // emit a TapGrid for a station that survives the modes filter but is
  // dropped server-side later (transient stale TapGrid), and rejecting the
  // orphan here is cheaper than crashing hitTest at click time.
  const filteredTapGrids: readonly SchemaTapGrid[] | undefined =
    data.tapGrids === undefined
      ? undefined
      : data.tapGrids.filter(
          (g) => g.modes.includes(mode) && stationIds.has(g.stationId.replace(/&#38;/g, '&')),
        )

  return {
    version: data.version,
    stations: filteredStations,
    connections: filteredConnections,
    additional: filteredAdditional,
    transitions: filteredTransitions,
    texts: filteredTexts,
    tapGrids: filteredTapGrids,
  }
}

function endpointSurvives(id: string | undefined, survivingIds: ReadonlySet<string>): boolean {
  if (id === undefined) return true
  return survivingIds.has(id)
}
